// Internal-only access gate. Rendered around the whole app when
// VITE_INTERNAL_ONLY === '1' (dev/stage). Restricts the SPA to seeded internal
// users. OTP-only (no password): enter your email, then a one-time code emailed
// to you. Once a device is verified it is trusted, so future logins on that
// browser/machine skip the OTP step.
//
// Unrecognized email → a "Request access" form (name + phone) that emails the
// platform admin. The admin signs in and, at ?internal=requests, reviews the
// queued requests and Approves/Rejects them. Self-contained (no app contexts).
import { useCallback, useEffect, useState } from 'react'

// Deployed builds call VITE_API_URL/api; local dev uses relative URLs.
// Domain origin only (strip any path VITE_API_URL may include, e.g. /api/v1),
// then append the app's actual route prefix below. This keeps the gate correct
// whether VITE_API_URL is "https://dev-api.x.com" or "https://dev-api.x.com/api".
let API_ORIGIN = ''
try {
  if (import.meta.env.PROD && import.meta.env.VITE_API_URL) {
    API_ORIGIN = new URL(import.meta.env.VITE_API_URL).origin
  }
} catch { API_ORIGIN = '' }
const API_PREFIX = '' // installer rewrites this to the app's actual route prefix
const API_BASE = `${API_ORIGIN}${API_PREFIX}`

const TOKEN_KEY = 'internal_access_token'
const DEVICE_ID_KEY = 'internal_access_device_id'

// Admin review page is requested via ?internal=requests (link in the email).
const REVIEW_MODE = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('internal') === 'requests'

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) { id = uuid(); localStorage.setItem(DEVICE_ID_KEY, id) }
  return id
}

async function sha256Hex(str) {
  const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

function fingerprintSource() {
  const n = window.navigator
  return [
    n.userAgent, n.language, n.platform,
    `${window.screen?.width}x${window.screen?.height}x${window.screen?.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join('|')
}

function deviceLabel() {
  const ua = window.navigator.userAgent
  const browser = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome'
    : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Browser'
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS'
    : /Android/.test(ua) ? 'Android' : /Linux/.test(ua) ? 'Linux'
    : /iPhone|iPad/.test(ua) ? 'iOS' : 'Unknown OS'
  return `${browser} on ${os}`
}

// ⚠ A THROWN FETCH MUST NOT REACH THE CALLER. The session probe now runs on
//   EVERY load rather than only when a token exists, so an offline visitor, a
//   VPN or a dead API would reject this promise inside the mount effect, no
//   phase would ever be set, and the gate would sit on "Checking your session…"
//   for ever. Before the probe moved, a visitor with no token never called the
//   API at all and could not hit this. Returning a sentinel instead lets the
//   caller fall through to the email form exactly as it used to.
async function call(path, { method = 'POST', body, token } = {}) {
  let res
  try {
    res = await fetch(`${API_BASE}/internal-access/${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  } catch {
    return { ok: false, status: 0, data: {}, networkError: true }
  }
  let data = {}
  try { data = await res.json() } catch { /* an HTML 500 body — ignore */ }
  return { ok: res.ok, status: res.status, data }
}

const post = (path, body, token) => call(path, { method: 'POST', body, token })

// Systeza's palette — the same values the app's --color-* / --bg-* / --text-*
// custom properties define, repeated as literals because this component is
// deliberately self-contained (see the porting note below) and must not depend
// on a stylesheet that may not have loaded.
//
// ⚠ MEASURED, NOT ASSUMED. Contrast against the card surface #040E2A:
//     text #EEF4FF ......... 17.27  AA
//     accent #00C8E8 ........ 9.47  AA
//     muted #7A90BC ......... 5.94  AA
//     primary #1A6AF5 ....... 4.02  AA-large only
//     prim-dark #1251D4 ..... 2.86  FAIL
//     dim #3A4F78 ........... 2.33  FAIL
//     border #1A2F60 ........ 1.47  FAIL — hairlines only, by design
//
// ⚠⚠ THE BRAND BLUE IS NOT A TEXT COLOUR HERE. --color-primary is the identity
//   and measures 4.02 on the card — fine for a fill or a large heading, not for
//   a small label. The cyan accent is 9.47 and does the talking instead. White
//   ON the blue fill is 4.75, so the button itself is fine; it is blue TYPE
//   that fails, which is the opposite way round from most of these products.
const BG       = '#020818' // --bg-base
const SURFACE  = '#040E2A' // --bg-surface
const ELEVATED = '#071640' // --bg-elevated
const TEXT     = '#EEF4FF' // --text-primary
const MUTED    = '#7A90BC' // --text-muted
const PRIMARY  = '#1A6AF5' // --color-primary — FILLS and large type only
const PRIM_DK  = '#1251D4' // --color-primary-dark
const ACCENT   = '#00C8E8' // --color-accent — the safe bright one
const ACCENT_DK = '#00A8C4'
const ERROR    = '#ef4444'

const DISPLAY = "Syne, ui-sans-serif, system-ui, sans-serif"
const UI = "'DM Sans', ui-sans-serif, system-ui, sans-serif"

const BLEED      = 40                        // how far the drawing grows past each edge
// ⚠ ON A DARK GROUND THE DRAWING'S INKS ARE TINTS OF THE TEXT, NOT THE INK.
//   --border-default (#1A2F60) is the token that looks right for placeholder
//   bars and is 1.47:1 — invisible before a blur and a scrim are applied on
//   top. An earlier attempt at this on a sibling product was built from exactly
//   that kind of token and rendered a ghost.
const GHOST      = 'rgba(238,244,255,0.17)'
const GHOST_SOFT = 'rgba(238,244,255,0.07)'
const EDGE       = 'rgba(238,244,255,0.14)'

// ── The app shell behind the dialog ───────────────────────────────────────
// Someone who lands on a closed door should still be able to see what is
// behind it. A flat void gives a visitor nothing to judge, and reads more like
// a parked domain than a product that is not open yet.
//
// ⚠⚠⚠ THIS IS A DRAWING, NOT THE APP, AND IT MUST STAY THAT WAY.
//   The obvious implementation — render `children` blurred underneath — is a
//   hole straight through the gate: the app would MOUNT and run every fetch it
//   makes on load for a visitor who has not been authorized, `filter: blur()`
//   is decoration that any inspector removes, and a business-operations product
//   holds other companies' contacts, invoices and documents, which would then
//   sit in the DOM of a page whose entire job is to withhold them. So this
//   renders fixed markup and nothing else: no router, no contexts, no fetches,
//   `aria-hidden` and `pointer-events: none`.
//
// ⚠ The module names are the product's OWN modules, read out of the codebase,
//   so they are description rather than invention. Everything inside the tiles
//   is a bar: no counts, no customers, no invoice numbers, no amounts.
function AppPreview() {
  const nav = ['Modules', 'Pricing', 'Roadmap', 'Docs']
  return (
    <div style={P.root} aria-hidden="true">
      {/* Inline styles cannot express a media query, and at phone width the
          module grid squeezes the hero column to nothing. It goes; the cards
          stay, stacked, so the lower half is not an empty slab. */}
      <style>{`
        @media (max-width: 880px) {
          .sy-ig-grid { display: none !important; }
          .sy-ig-cards { flex-direction: column !important; }
          .sy-ig-hero { padding-top: 34px !important; }
          .sy-ig-title { font-size: 32px !important; }
        }
      `}</style>

      <div style={P.nav}>
        <div style={P.navInner}>
          <span style={P.wordmark}>Systeza</span>
          <div style={P.navLinks}>
            {nav.map((l) => <span key={l} style={P.navLink}>{l}</span>)}
          </div>
          <span style={P.navBtn}>Request access</span>
        </div>
      </div>

      <div style={P.hero} className="sy-ig-hero">
        <div style={P.heroCopy}>
          <span style={P.eyebrow}>Launching soon</span>
          <h2 style={P.heroTitle} className="sy-ig-title">
            Systems that <em style={P.heroEm}>just work.</em>
          </h2>
          <div style={{ ...P.bar, width: '90%' }} />
          <div style={{ ...P.bar, width: '64%' }} />
          <div style={P.heroBtns}>
            <span style={P.btnSolid} />
            <span style={P.btnGhost} />
          </div>
        </div>
        <ModuleGrid />
      </div>

      <div style={P.cardRow} className="sy-ig-cards">
        {[PRIMARY, ACCENT, PRIM_DK].map((c) => (
          <div key={c} style={P.card}>
            <div style={{ ...P.cardIcon, background: `${c}2E`, border: `1px solid ${c}59` }} />
            <div style={{ ...P.bar, width: '72%', height: 11 }} />
            <div style={{ ...P.bar, width: '48%', height: 11, margin: 0 }} />
          </div>
        ))}
      </div>
    </div>
  )
}

// The module grid — a product sold as modules is recognised by its modules.
// These are the app's own eight, with a colour chip and a bar each. Fixed
// order, not random: a backdrop that reshuffles between renders is a
// distraction.
function ModuleGrid() {
  const MODULES = [
    { n: 'CRM', c: PRIMARY }, { n: 'Contacts', c: ACCENT },
    { n: 'Invoices', c: PRIMARY }, { n: 'Projects', c: ACCENT_DK },
    { n: 'Tasks', c: PRIM_DK }, { n: 'Documents', c: ACCENT },
    { n: 'Reports', c: PRIMARY }, { n: 'Support', c: ACCENT_DK },
  ]
  return (
    <div style={P.grid} className="sy-ig-grid">
      <div style={P.gridChrome}>
        {[PRIMARY, ACCENT, ACCENT_DK].map((c) => (
          <span key={c} style={{ ...P.dot, background: c }} />
        ))}
      </div>
      <div style={P.gridBody}>
        {MODULES.map((m) => (
          <div key={m.n} style={P.tile}>
            <span style={{ ...P.tileChip, background: `${m.c}33`, border: `1.5px solid ${m.c}80` }} />
            <span style={P.tileName}>{m.n}</span>
            <span style={{ ...P.bar, height: 5, width: '64%', margin: '6px 0 0' }} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default function InternalGate({ children }) {
  // Self-disabling: outside dev/stage (flag != '1') this is a passthrough, so
  // it can be wired into every app's entry unconditionally. The flag is inlined
  // at build time, so this branch is constant per build (hooks stay consistent).
  if (import.meta.env.VITE_INTERNAL_ONLY !== '1') return children

  const [phase, setPhase] = useState('checking') // checking | email | otp | request | requested | authed | disabled
  const [deviceHash, setDeviceHash] = useState(null)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [phone, setPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let alive = true
    ;(async () => {
      const dh = await sha256Hex(`${getDeviceId()}|${fingerprintSource()}`)
      if (!alive) return
      setDeviceHash(dh)
      const token = localStorage.getItem(TOKEN_KEY)

      // ⚠⚠⚠ ASK THE BACKEND WHETHER THE GATE IS ON, AND STAND DOWN IF IT IS NOT.
      //   InternalAccessController::ensureEnabled() returns 404 on every gate
      //   endpoint when INTERNAL_ONLY is off. Before this, with no token the
      //   gate jumped straight to the email form without asking the backend
      //   anything, then 404ed on `request-otp` and printed the raw envelope
      //   "The requested resource was not found." under the email field — a
      //   production site nobody could get past, including the admin,
      //   reporting a missing route rather than a disabled feature. This exact
      //   failure took a sibling product's production site down.
      //
      //   A 404 from a gate endpoint is not an error. It is the backend saying
      //   "I do not have this gate", and the only correct response is to let
      //   the app through. The probe is safe without a token because
      //   ensureEnabled() is the first line of session(), ahead of validation
      //   and the bearer check.
      const probe = await post('session', { device_hash: dh }, token || undefined)
      if (!alive) return
      if (probe.status === 404) { setPhase('disabled'); return }

      if (!token) { setPhase('email'); return }
      const { ok, data } = probe
      if (ok && data.valid) { setIsAdmin(!!data.user?.is_admin); setPhase('authed') }
      else { localStorage.removeItem(TOKEN_KEY); setPhase('email') }
    })()
    return () => { alive = false }
  }, [])

  const requestCode = useCallback(async () => {
    return post('request-otp', {
      email: email.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    })
  }, [email, deviceHash])

  const submitEmail = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError(''); setNotice('')
    const { ok, status, data } = await requestCode()
    setBusy(false)
    if (!ok) {
      // Same reasoning as the mount probe: 404 here is "no gate", not an error.
      if (status === 404) { setPhase('disabled'); return }
      if (data.can_request) { setPhase('request'); return }
      // Already applied and awaiting approval (or still to confirm their email):
      // tell them where they stand instead of pushing them back to the form.
      if (data.pending) { setNotice(data.message || 'Your request is awaiting approval.'); return }
      setError(data.message || 'Could not sign in.'); return
    }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed'); return
    }
    setNotice(`We emailed a 6-digit code to ${data.email || email}.`)
    setPhase('otp')
  }, [requestCode, email])

  const submitOtp = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    const { ok, data } = await post('verify-otp', {
      email: email.trim(), code: code.trim(), device_hash: deviceHash, device_label: deviceLabel(),
    })
    setBusy(false)
    if (!ok) { setError(data.message || 'Invalid code.'); return }
    localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed')
  }, [email, code, deviceHash])

  const resend = useCallback(async () => {
    setBusy(true); setError(''); setNotice('')
    const { ok, data } = await requestCode()
    setBusy(false)
    if (!ok) { setError(data.message || 'Could not resend code.'); return }
    if (data.status === 'authenticated') {
      localStorage.setItem(TOKEN_KEY, data.token); setIsAdmin(!!data.user?.is_admin); setPhase('authed'); return
    }
    setNotice('A new code is on its way.')
  }, [requestCode])

  const submitRequest = useCallback(async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    const { ok, data } = await post('request-access', {
      email: email.trim(), first_name: first.trim(), last_name: last.trim(), phone: phone.trim(), whatsapp: whatsapp.trim(),
    })
    setBusy(false)
    if (!ok) { setError(data.message || 'Could not submit your request.'); return }
    if (data.status === 'already_authorized') {
      setNotice('You already have access — sign in with your email.'); setPhase('email'); return
    }
    setPhase('requested')
  }, [email, first, last, phone])

  // The backend has the gate switched off. Render the app.
  if (phase === 'disabled') return children

  if (phase === 'authed') {
    if (REVIEW_MODE && isAdmin) return <ReviewPage token={localStorage.getItem(TOKEN_KEY)} />
    return children
  }

  return (
    <div style={S.wrap}>
      <AppPreview />
      {/* A wash over the drawing: enough to hold the dialog contrast
          without flattening the shell back into the void it replaced. */}
      <div style={S.scrim} aria-hidden="true" />
      <div style={S.card}>
        <div style={S.brandRow}>
          <span style={S.badge}>{import.meta.env.VITE_APP_NAME || 'App'}</span>
          <span style={S.internalTag}>Internal access</span>
        </div>

        {phase === 'checking' && <div style={S.checking}>Checking your session…</div>}

        {phase === 'email' && (
          <form onSubmit={submitEmail}>
            <h1 style={S.title}>Sign in to continue</h1>
            <p style={S.sub}>This environment is restricted to the internal team. Enter your email and we’ll send you a one-time code.</p>
            <label style={S.label}>Email</label>
            <input style={S.input} type="email" autoFocus required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Please wait…' : 'Send code'}
            </button>
          </form>
        )}

        {phase === 'otp' && (
          <form onSubmit={submitOtp}>
            <h1 style={S.title}>Enter your code</h1>
            {notice && <p style={S.sub}>{notice}</p>}
            <label style={S.label}>6-digit code</label>
            <input style={{ ...S.input, letterSpacing: '0.4em', textAlign: 'center', fontSize: 20 }}
              inputMode="numeric" autoFocus required value={code} maxLength={6}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" />
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <div style={S.row}>
              <button type="button" style={S.linkBtn} onClick={resend} disabled={busy}>Resend code</button>
              <button type="button" style={S.linkBtn} onClick={() => { setPhase('email'); setCode(''); setError('') }}>
                Use a different email
              </button>
            </div>
          </form>
        )}

        {phase === 'request' && (
          <form onSubmit={submitRequest}>
            <h1 style={S.title}>Request access</h1>
            <p style={S.sub}>Your email isn’t on the internal list yet. Send a request and the platform admin will review it.</p>
            <label style={S.label}>Email address</label>
            <input style={{ ...S.input, opacity: 0.65, cursor: 'not-allowed' }} type="email" value={email} disabled readOnly />
            <div style={S.grid2}>
              <div>
                <label style={S.label}>First name</label>
                <input style={S.input} autoFocus required value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First" />
              </div>
              <div>
                <label style={S.label}>Last name</label>
                <input style={S.input} required value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last" />
              </div>
            </div>
            <label style={S.label}>Contact phone number</label>
            <input style={S.input} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" />
            <label style={S.label}>WhatsApp number</label>
            <input style={S.input} type="tel" required value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+1 555 123 4567" />
            <div style={S.hint || { fontSize: 12, opacity: 0.7, marginTop: 4 }}>Include your country code, starting with +. This is how we'll reach you.</div>
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} type="submit">
              {busy ? 'Sending…' : 'Request access'}
            </button>
            <div style={S.row}>
              <button type="button" style={S.linkBtn} onClick={() => { setPhase('email'); setError('') }}>
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {phase === 'requested' && (
          <div>
            <h1 style={S.title}>Request sent — check your email</h1>
            <p style={S.sub}>Thanks. We’ve emailed you a link to <strong>confirm your email address</strong> — please open it and press “Confirm my email” so we know you’re reachable. We’ll then review your request, and once it’s approved you’ll be able to sign in with a one-time code.</p>
            <button style={S.btn} type="button" onClick={() => { setPhase('email'); setFirst(''); setLast(''); setPhone(''); setError('') }}>
              Done
            </button>
          </div>
        )}

        <div style={S.foot}>Trusted devices skip the code next time · A Stacza product</div>
      </div>
    </div>
  )
}

// ── Admin review page (?internal=requests) ─────────────────────────────────
function ReviewPage({ token }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')
  const [actingId, setActingId] = useState(null)

  const load = useCallback(async () => {
    const { ok, data } = await call('requests', { method: 'GET', token })
    if (!ok) { setError(data.message || 'Could not load requests.'); setRows([]); return }
    setRows(data.data || [])
  }, [token])

  useEffect(() => { load() }, [load])

  const act = useCallback(async (id, action) => {
    setActingId(id); setError('')
    const { ok, data } = await post(`requests/${id}/${action}`, {}, token)
    setActingId(null)
    if (!ok) { setError(data.message || `Could not ${action}.`); return }
    load()
  }, [token, load])

  const fmtDate = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
  }

  return (
    <div style={{ ...S.wrap, alignItems: 'flex-start', padding: '40px 20px' }}>
      <div style={{ ...S.card, maxWidth: 860 }}>
        <div style={S.brandRow}>
          <span style={S.badge}>{import.meta.env.VITE_APP_NAME || 'App'}</span>
          <span style={S.internalTag}>Access requests</span>
        </div>
        <h1 style={S.title}>Access requests</h1>
        <p style={S.sub}>Newest first. Approving grants access in this environment and adds the email to the seeder so it promotes to the others.</p>
        {error && <div style={S.error}>{error}</div>}

        {rows === null && <div style={S.checking}>Loading…</div>}
        {rows !== null && rows.length === 0 && <div style={S.checking}>No access requests yet.</div>}

        {rows !== null && rows.length > 0 && (
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Requester</th>
                  <th style={S.th}>Phone</th>
                  <th style={S.th}>Requested</th>
                  <th style={S.th}>Status</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{`${r.first_name} ${r.last_name}`.trim() || '—'}</div>
                      <div style={S.tdSub}>{r.email}</div>
                    </td>
                    <td style={S.td}>{r.phone || '—'}</td>
                    <td style={S.td}>{fmtDate(r.created_at)}</td>
                    <td style={S.td}><StatusBadge status={r.status} /></td>
                    <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.status === 'pending' ? (
                        <>
                          <button style={S.approveBtn} disabled={actingId === r.id} onClick={() => act(r.id, 'approve')}>Approve</button>
                          <button style={S.rejectBtn} disabled={actingId === r.id} onClick={() => act(r.id, 'reject')}>Reject</button>
                        </>
                      ) : (
                        <span style={S.tdSub}>
                          {r.reviewed_by ? `by ${r.reviewed_by}` : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={S.foot}>
          <a href={window.location.pathname} style={{ color: MUTED }}>← Back to app</a>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }) {
  const map = {
    pending:  { bg: `${PRIMARY}1A`, bd: `${PRIMARY}4D`, c: ACCENT, t: 'Pending' },
    approved: { bg: `${ACCENT}14`,  bd: `${ACCENT}4D`,  c: ACCENT, t: 'Approved' },
    rejected: { bg: `${ERROR}1A`,   bd: `${ERROR}4D`,   c: ERROR,  t: 'Rejected' },
  }
  const s = map[status] || map.pending
  return (
    <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.c }}>{s.t}</span>
  )
}

// ── The drawing behind the dialog ─────────────────────────────────────────
// `pointer-events: none` on the root is load-bearing, not cosmetic: it is what
// stops a visitor interacting with anything here, including selecting text.
const P = {
  // ⚠ A BLEED MARGIN, NOT A SCALE. `transform: scale()` also pushes the blur's
  //   soft edge off-screen, but it does it by enlarging about the centre, which
  //   crops the top of the viewport and slices the navbar in half.
  root: {
    position: 'fixed', inset: -BLEED, overflow: 'hidden', pointerEvents: 'none',
    userSelect: 'none', fontFamily: UI, padding: `${BLEED}px ${BLEED}px 0`,
    boxSizing: 'border-box', filter: 'blur(3.5px) saturate(1.05)',
    background: `radial-gradient(110% 80% at 80% 4%, ${PRIMARY}26 0%, ${PRIMARY}00 55%),
                 radial-gradient(90% 70% at 8% 92%, ${ACCENT}1A 0%, ${ACCENT}00 60%),
                 linear-gradient(180deg, ${BG} 0%, ${SURFACE} 70%, ${BG} 100%)`,
  },
  nav: { background: `${BG}D9`, borderBottom: `1px solid ${EDGE}` },
  navInner: { maxWidth: 1180, margin: '0 auto', padding: '0 28px', height: 68, display: 'flex', alignItems: 'center', gap: 26 },
  wordmark: { fontFamily: DISPLAY, fontWeight: 800, fontSize: 21, color: TEXT, letterSpacing: '-0.02em' },
  navLinks: { display: 'flex', gap: 20, flex: 1, flexWrap: 'nowrap', overflow: 'hidden' },
  navLink: { fontSize: 13.5, color: MUTED, whiteSpace: 'nowrap' },
  // White on the blue fill is 4.75 — the label clears AA even though blue TYPE
  // would not.
  navBtn: { fontSize: 13, fontWeight: 700, color: '#FFFFFF', whiteSpace: 'nowrap', background: PRIMARY, padding: '10px 18px', borderRadius: 8 },

  hero: { maxWidth: 1180, margin: '0 auto', padding: '54px 28px 36px', display: 'flex', alignItems: 'center', gap: 44 },
  heroCopy: { flex: 1, minWidth: 0 },
  eyebrow: { display: 'inline-block', fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: ACCENT, background: `${ACCENT}14`, border: `1px solid ${ACCENT}3D`, padding: '5px 12px', borderRadius: 999, marginBottom: 18 },
  heroTitle: { fontFamily: DISPLAY, fontSize: 46, lineHeight: 1.08, color: TEXT, margin: '0 0 22px', fontWeight: 800, letterSpacing: '-0.03em' },
  // The landing page gradients this phrase from primary to accent.
  heroEm: { fontStyle: 'normal', backgroundImage: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' },
  bar: { height: 12, borderRadius: 999, background: GHOST, margin: '0 0 10px' },
  heroBtns: { display: 'flex', gap: 12, marginTop: 26 },
  btnSolid: { width: 156, height: 46, borderRadius: 9, background: `linear-gradient(120deg, ${PRIMARY}, ${PRIM_DK})` },
  btnGhost: { width: 132, height: 46, borderRadius: 9, border: `1.5px solid ${MUTED}` },

  grid: { flexShrink: 0, width: 330, background: SURFACE, borderRadius: 13, border: `1px solid ${EDGE}`, overflow: 'hidden', boxShadow: '0 24px 54px rgba(0,0,0,0.6)' },
  gridChrome: { height: 30, background: ELEVATED, borderBottom: `1px solid ${EDGE}`, display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px' },
  dot: { width: 9, height: 9, borderRadius: 999, display: 'inline-block' },
  gridBody: { padding: 13, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 },
  tile: { background: ELEVATED, border: `1px solid ${EDGE}`, borderRadius: 9, padding: 10 },
  tileChip: { width: 20, height: 20, borderRadius: 6, display: 'block', marginBottom: 7 },
  tileName: { fontSize: 11, fontWeight: 600, color: TEXT, display: 'block' },

  cardRow: { maxWidth: 1180, margin: '0 auto', padding: '0 28px', display: 'flex', gap: 20 },
  card: { flex: 1, background: SURFACE, border: `1px solid ${EDGE}`, borderRadius: 13, padding: 18, boxShadow: '0 14px 32px rgba(0,0,0,0.4)' },
  cardIcon: { width: 38, height: 38, borderRadius: 9, marginBottom: 16 },
}

const S = {
  wrap: { position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: BG, fontFamily: UI, color: TEXT },
  // ⚠ A WASH, not a lid: an early version of this on a sibling product sat at
  // 94% opacity in the centre and erased the shell it was meant to reveal.
  scrim: { position: 'fixed', inset: 0, pointerEvents: 'none', background: `radial-gradient(ellipse at center, ${BG}C7 0%, ${BG}99 45%, ${BG}54 100%)` },
  card: { position: 'relative', zIndex: 1, width: '100%', maxWidth: 400, background: SURFACE, border: `1px solid ${EDGE}`, borderRadius: 16, padding: '32px 28px', boxShadow: '0 40px 90px rgba(0,0,0,0.75), 0 2px 10px rgba(0,0,0,0.5)' },
  brandRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 },
  badge: { fontFamily: DISPLAY, fontWeight: 800, fontSize: 20, color: TEXT, letterSpacing: '-0.02em' },
  // ⚠ ACCENT, not PRIMARY. The brand blue is 4.02 on this card and this chip is
  //   the only thing stating what kind of page it is; the cyan is 9.47.
  internalTag: { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.3, color: ACCENT, border: `1px solid ${ACCENT}4D`, background: `${ACCENT}14`, padding: '4px 10px', borderRadius: 999 },
  checking: { padding: '28px 0', textAlign: 'center', color: MUTED, fontSize: 14 },
  title: { fontFamily: DISPLAY, fontSize: 25, fontWeight: 700, margin: '0 0 8px', color: TEXT, letterSpacing: '-0.02em' },
  sub: { fontSize: 13.5, color: MUTED, margin: '0 0 22px', lineHeight: 1.5 },
  label: { display: 'block', fontSize: 12, color: MUTED, margin: '14px 0 6px', fontWeight: 600 },
  // ⚠ The stroke is MUTED (5.94). WCAG 1.4.11 asks 3.0 of a control's boundary;
  //   --border-default is 1.47, far under it, and that boundary is the only
  //   thing showing where the field is.
  input: { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 9, border: `1px solid ${MUTED}`, background: ELEVATED, color: TEXT, fontSize: 14, outline: 'none', fontFamily: UI },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  option: { color: '#111827', background: '#FFFFFF' },
  hint: { fontSize: 11.5, color: MUTED, marginTop: 6 },
  checkRow: { display: 'flex', gap: 10, alignItems: 'flex-start', margin: '16px 0 2px', fontSize: 12.5, lineHeight: 1.45, color: MUTED, cursor: 'pointer' },
  btn: { width: '100%', marginTop: 22, padding: '13px 18px', borderRadius: 9, border: 'none', background: `linear-gradient(120deg, ${PRIMARY}, ${PRIM_DK})`, color: '#FFFFFF', fontWeight: 700, fontSize: 14.5, cursor: 'pointer', fontFamily: UI },
  row: { display: 'flex', justifyContent: 'space-between', marginTop: 16 },
  linkBtn: { background: 'none', border: 'none', color: ACCENT, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontFamily: UI },
  error: { marginTop: 14, fontSize: 13, color: ERROR, background: `${ERROR}1A`, border: `1px solid ${ERROR}4D`, borderRadius: 8, padding: '9px 12px' },
  foot: { marginTop: 26, fontSize: 11, color: MUTED, textAlign: 'center' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.6, color: MUTED, borderBottom: `1px solid ${EDGE}` },
  td: { padding: '12px', borderBottom: `1px solid ${EDGE}`, verticalAlign: 'top' },
  tdSub: { fontSize: 12, color: MUTED },
  approveBtn: { padding: '7px 13px', marginLeft: 6, borderRadius: 8, border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FFFFFF', fontWeight: 700, fontSize: 12.5, fontFamily: UI },
  rejectBtn: { padding: '7px 13px', marginLeft: 6, borderRadius: 8, cursor: 'pointer', background: 'transparent', border: `1px solid ${ERROR}66`, color: ERROR, fontWeight: 600, fontSize: 12.5, fontFamily: UI },
}
